/**
 * 头像生成工具函数
 * 提供统一的头像URL生成和降级方案
 */

export interface AvatarOptions {
  name: string;
  size?: number;
  background?: string;
  color?: string;
  bold?: boolean;
}

/**
 * 生成头像URL
 * @param options 头像配置选项
 * @returns 头像URL
 */
export function generateAvatarUrl(options: AvatarOptions): string {
  const {
    name,
    size = 128,
    background = '4F46E5', // 默认紫色
    color = 'fff', // 默认白色
    bold = true,
  } = options;

  // 使用 UI Avatars（国内可访问）
  const params = new URLSearchParams({
    name,
    size: size.toString(),
    background,
    color,
    bold: bold.toString(),
    rounded: 'true', // 圆角
  });

  return `https://ui-avatars.com/api/?${params.toString()}`;
}

/**
 * 获取默认头像（降级方案）
 */
export function getDefaultAvatar(): string {
  return '/assets/default-avatar.png';
}

/**
 * 头像加载错误处理
 * @param event 错误事件
 */
export function handleAvatarError(event: React.SyntheticEvent<HTMLImageElement>) {
  const img = event.currentTarget;
  // 如果加载失败，使用默认头像或生成首字母头像
  if (!img.dataset.fallback) {
    img.dataset.fallback = 'true';
    img.src = generateAvatarUrl({ name: '?' });
  }
}

/**
 * 从用户名生成头像URL
 * @param username 用户名
 * @param customBg 自定义背景色（可选）
 */
export function getUserAvatar(username: string, customBg?: string): string {
  // 为不同用户生成不同颜色
  const colors = [
    '4F46E5', // 紫色
    '10B981', // 绿色
    'F59E0B', // 橙色
    'EF4444', // 红色
    '3B82F6', // 蓝色
    '8B5CF6', // 紫罗兰
    'EC4899', // 粉色
    '06B6D4', // 青色
  ];

  const hash = username.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const background = customBg || colors[hash % colors.length];

  return generateAvatarUrl({
    name: username,
    background,
  });
}
